import type { PropsWithChildren } from "react";
import { Navigate } from "react-router-dom";
import { ROUTES } from "../constants/routes";
import { useActorRole } from "../hooks/useOwnerContext";
import { LoadingSkeleton } from "./LoadingSkeleton";

/**
 * Wraps the personal tracking pages (Observations, Bulk Entry, Getting Started, and the personal
 * Calendar / Insights / History). Instructor accounts have no tracking data of their own, so they
 * are sent to the roster; until the role is known nothing role-specific renders, so an instructor
 * never sees a frame of tracking UI (at most this neutral skeleton while the redirect commits).
 *
 * Tracking and viewing accounts pass straight through: with a resolved context or the persisted
 * role hint (every returning user) the page renders on the first frame exactly as before. Only a
 * cold load with no hint shows the skeleton, and only until the same owner context those pages
 * already wait on resolves.
 */
export const TrackingRouteGate = ({ children }: PropsWithChildren) => {
  const { role, isResolved } = useActorRole();

  if (role === "instructor") {
    // <Navigate> (a passive effect) rather than a layout effect: on a cold load the router's
    // history listener isn't attached until after children's layout effects, so an earlier
    // navigate() would be dropped. The page underneath is never mounted either way.
    return (
      <>
        <Navigate to={ROUTES.instructorClients} replace />
        <LoadingSkeleton />
      </>
    );
  }

  if (!isResolved) {
    return <LoadingSkeleton />;
  }

  return <>{children}</>;
};
